import { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapView.css';
import api from './api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// ── 쿠키 헬퍼 ────────────────────────────────────────────────
function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// ── 타일 스타일 ──────────────────────────────────────────────
const TILE_STYLES = [
  { id: 'minimal',   label: 'MINIMAL',   url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',       attribution: '&copy; <a href="https://carto.com/">CARTO</a> &copy; OSM' },
  { id: 'dark',      label: 'DARK',      url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',        attribution: '&copy; <a href="https://carto.com/">CARTO</a> &copy; OSM' },
  { id: 'satellite', label: 'SATELLITE', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; <a href="https://www.esri.com/">Esri</a>' },
  { id: 'street',    label: 'STREET',    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',                   attribution: '&copy; <a href="https://www.openstreetmap.org/">OSM</a>' },
];

// groupName별 폴리라인 색상 순환
const GROUP_COLORS  = ['#4d9fff', '#ff6b6b', '#ffd93d', '#6bcb77', '#c77dff', '#ff9f43', '#00d4c8', '#ff9de2'];
const STATUS_LABELS = { 0: '대기', 1: '승인', 2: '운항중', 3: '완료', 4: '취소' };

// ── 아이콘 ────────────────────────────────────────────────────
function uavIcon(id, status, isSelected, alertLevel) {
  const baseColor = status === 'SIGNAL LOST' ? '#ff4d4d' : '#00d46a';
  const bgColor   = alertLevel === 'danger'  ? '#ff4d4d'
                  : alertLevel === 'warning' ? '#fbbf24'
                  : 'rgba(8,16,30,0.75)';
  const wrapClass = `uav-icon-wrap${isSelected ? ' uav-selected' : ''}`;
  return L.divIcon({
    className: '',
    html: `<div class="uav-icon-root">
             <div class="${wrapClass}" style="border-color:${baseColor};background:${bgColor}">
               <img src="/drone.png" class="uav-icon-img" alt="UAV" />
             </div>
             <span class="uav-icon-label" style="color:${baseColor}">${id}</span>
           </div>`,
    iconSize: [60, 52], iconAnchor: [30, 18], popupAnchor: [0, -22],
  });
}

function calcDist(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function vtpIcon(status, selected) {
  const statusColor = status === 1 ? '#00d46a' : '#f0b429';
  const borderColor = '#38bdf8';
  const size  = selected ? 40 : 30;
  const glow  = selected ? `0 0 16px ${borderColor}, 0 0 6px ${statusColor}88` : `0 0 8px ${borderColor}88`;
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;border:${selected ? '2px' : '1.5px'} solid ${borderColor};box-shadow:${glow};display:flex;align-items:center;justify-content:center;background:#f0f4ff;overflow:hidden;">
             <img src="/vertiport.png" style="width:${size - 4}px;height:${size - 4}px;object-fit:contain;" alt="VTP" />
           </div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2],
  });
}

function fixIcon(label) {
  return L.divIcon({
    className: '',
    html: `<div class="fix-marker-wrap"><div class="fix-dot">◆</div><span class="fix-label">${label || ''}</span></div>`,
    iconSize: [90, 34], iconAnchor: [45, 8], popupAnchor: [0, -10],
  });
}

function routeFixIcon(label) {
  return L.divIcon({
    className: '',
    html: `<div class="rfix-wrap"><div class="rfix-dot">◆</div><span class="rfix-name">${label || ''}</span></div>`,
    iconSize: [90, 34], iconAnchor: [45, 8], popupAnchor: [0, -10],
  });
}

function airwayLabelIcon(groupName, segName, color) {
  const boxText = groupName || segName;
  const subText = groupName ? segName : '';
  return L.divIcon({
    className: '',
    html: `<div class="airway-label-wrap">
             <span class="airway-label" style="border-color:${color}44;color:${color}">${boxText}</span>
             ${subText ? `<span class="airway-seg-name" style="color:${color}bb">${subText}</span>` : ''}
           </div>`,
    iconSize: [0, 0], iconAnchor: [0, 0],
  });
}

// ── 마우스 좌표 추적 ──────────────────────────────────────────
function MouseCoords({ onMove, onContextMenu, onClose, ctxTargetRef }) {
  useMapEvents({
    mousemove:   e => onMove(e.latlng),
    mouseout:    () => onMove(null),
    contextmenu: e => {
      e.originalEvent.preventDefault();
      if (ctxTargetRef.current) { ctxTargetRef.current = null; return; }
      onContextMenu(e);
    },
    click: () => { ctxTargetRef.current = null; onClose(); },
  });
  return null;
}

// ── FIX 등록 모달 ─────────────────────────────────────────────
function FixCreateModal({ latlng, onSave, onCancel }) {
  const [fixId, setFixId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSave() {
    if (!fixId.trim()) { setErr('FIX ID를 입력하세요.'); return; }
    setSaving(true);
    try { await onSave({ fixId: fixId.trim() }); }
    catch (e) { setErr(e.response?.data?.message || '등록 실패'); setSaving(false); }
  }

  return (
    <div className="fix-modal-overlay">
      <div className="fix-modal">
        <div className="fix-modal-hd">FIX 포인트 등록</div>
        <div className="fix-modal-coords">{latlng.lat.toFixed(6)}, {latlng.lng.toFixed(6)}</div>
        <input className="fix-modal-input" placeholder="FIX ID (예: ABCDE)"
          value={fixId} onChange={e => setFixId(e.target.value)} autoFocus
          onKeyDown={e => e.key === 'Enter' && handleSave()} />
        {err && <p className="fix-modal-err">{err}</p>}
        <div className="fix-modal-btns">
          <button className="fix-modal-save" onClick={handleSave} disabled={saving}>{saving ? '등록 중...' : '등록'}</button>
          <button className="fix-modal-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
}

// ── FIX 수정 모달 ─────────────────────────────────────────────
function FixEditModal({ fix, onSave, onCancel }) {
  const [fixId, setFixId] = useState(fix.fixId);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSave() {
    if (!fixId.trim()) { setErr('FIX ID를 입력하세요.'); return; }
    setSaving(true);
    try { await onSave({ fixId: fixId.trim() }); }
    catch (e) { setErr(e.response?.data?.message || '수정 실패'); setSaving(false); }
  }

  return (
    <div className="fix-modal-overlay">
      <div className="fix-modal">
        <div className="fix-modal-hd">FIX 수정</div>
        <div className="fix-modal-coords">{Number(fix.lat).toFixed(6)}, {Number(fix.lng).toFixed(6)}</div>
        <input className="fix-modal-input" placeholder="FIX ID"
          value={fixId} onChange={e => setFixId(e.target.value)} autoFocus
          onKeyDown={e => e.key === 'Enter' && handleSave()} />
        {err && <p className="fix-modal-err">{err}</p>}
        <div className="fix-modal-btns">
          <button className="fix-modal-save" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
          <button className="fix-modal-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
}

// ── 버티포트 수정 모달 ────────────────────────────────────────
function VtpEditModal({ vtp, onSave, onCancel }) {
  const [name, setName] = useState(vtp.name);
  const [status, setStatus] = useState(vtp.status);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSave() {
    if (!name.trim()) { setErr('이름을 입력하세요.'); return; }
    setSaving(true);
    try { await onSave({ name: name.trim(), status }); }
    catch (e) { setErr(e.response?.data?.message || '수정 실패'); setSaving(false); }
  }

  return (
    <div className="fix-modal-overlay">
      <div className="fix-modal">
        <div className="fix-modal-hd">버티포트 수정</div>
        <div className="fix-modal-coords">{vtp.vertiportId}</div>
        <input className="fix-modal-input" placeholder="이름"
          value={name} onChange={e => setName(e.target.value)} autoFocus
          onKeyDown={e => e.key === 'Enter' && handleSave()} />
        <div className="fix-modal-status-row">
          <span className="fix-modal-status-lbl">상태</span>
          <button className={`fix-modal-status-btn${status === 1 ? ' active' : ''}`} onClick={() => setStatus(1)}>운영중</button>
          <button className={`fix-modal-status-btn${status === 0 ? ' standby' : ''}`} onClick={() => setStatus(0)}>대기</button>
        </div>
        {err && <p className="fix-modal-err">{err}</p>}
        <div className="fix-modal-btns">
          <button className="fix-modal-save" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
          <button className="fix-modal-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
}

// ── Airway 이름 입력 모달 (LINK 모드에서 생성) ────────────────
function AirwayNameModal({ from, to, existingGroups, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSave() {
    if (!name.trim()) { setErr('Airway 이름을 입력하세요.'); return; }
    setSaving(true);
    try { await onSave(name.trim(), groupName.trim()); }
    catch (e) { setErr(e.response?.data?.message || '생성 실패'); setSaving(false); }
  }

  return (
    <div className="fix-modal-overlay">
      <div className="fix-modal">
        <div className="fix-modal-hd">Airway 생성</div>
        <div className="fix-modal-coords">{from.name} → {to.name}</div>
        <input className="fix-modal-input" placeholder="Airway 이름 (구간명)"
          value={name} onChange={e => setName(e.target.value)} autoFocus
          onKeyDown={e => e.key === 'Enter' && handleSave()} />
        <input className="fix-modal-input" placeholder="그룹명 (노선명, 선택)"
          value={groupName} onChange={e => setGroupName(e.target.value)}
          list="aw-groups" />
        <datalist id="aw-groups">
          {existingGroups.map(g => <option key={g} value={g} />)}
        </datalist>
        {err && <p className="fix-modal-err">{err}</p>}
        <div className="fix-modal-btns">
          <button className="fix-modal-save" onClick={handleSave} disabled={saving}>{saving ? '생성 중...' : '생성'}</button>
          <button className="fix-modal-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
}

// ── Airway 이름 수정 모달 ─────────────────────────────────────
function AirwayEditModal({ airway, existingGroups, onSave, onCancel }) {
  const [name,      setName]      = useState(airway.name);
  const [groupName, setGroupName] = useState(airway.groupName || '');
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState('');

  async function handleSave() {
    if (!name.trim()) { setErr('이름을 입력하세요.'); return; }
    setSaving(true);
    try { await onSave(name.trim(), groupName.trim()); }
    catch (e) { setErr(e.response?.data?.message || '수정 실패'); setSaving(false); }
  }

  return (
    <div className="fix-modal-overlay">
      <div className="fix-modal">
        <div className="fix-modal-hd">Airway 수정</div>
        <div className="fix-modal-coords">{airway.from.name} → {airway.to.name}</div>
        <input className="fix-modal-input" placeholder="Airway 이름 (구간명)"
          value={name} onChange={e => setName(e.target.value)} autoFocus
          onKeyDown={e => e.key === 'Enter' && handleSave()} />
        <input className="fix-modal-input" placeholder="그룹명 (노선명, 선택)"
          value={groupName} onChange={e => setGroupName(e.target.value)}
          list="aw-edit-groups" />
        <datalist id="aw-edit-groups">
          {existingGroups.map(g => <option key={g} value={g} />)}
        </datalist>
        {err && <p className="fix-modal-err">{err}</p>}
        <div className="fix-modal-btns">
          <button className="fix-modal-save" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
          <button className="fix-modal-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
}

// ── 삭제 확인 모달 (연결 Airway 경고 포함) ────────────────────
function DeleteConfirmModal({ entityType, entityName, connectedAirways, onConfirm, onCancel }) {
  const [deleting, setDeleting] = useState(false);
  const label = entityType === 'fix' ? 'FIX' : '버티포트';

  return (
    <div className="fix-modal-overlay">
      <div className="fix-modal">
        <div className="fix-modal-hd">{label} 삭제</div>
        <p className="del-modal-msg"><strong>{entityName}</strong>을(를) 삭제하시겠습니까?</p>
        {connectedAirways.length > 0 && (
          <>
            <p className="del-modal-warn">
              ⚠ 연결된 Airway {connectedAirways.length}개도 함께 삭제됩니다.
            </p>
            <ul className="del-modal-list">
              {connectedAirways.map(aw => (
                <li key={aw.airwayUid} className="del-modal-item">
                  <span className="del-modal-name">{aw.name}</span>
                  <span className="del-modal-nodes">{aw.from.name} → {aw.to.name}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="fix-modal-btns">
          <button className="fix-modal-save fix-modal-del" disabled={deleting}
            onClick={async () => { setDeleting(true); await onConfirm(); }}>
            {deleting ? '삭제 중...' : '삭제'}
          </button>
          <button className="fix-modal-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
}

const CENTER = [35.95, 128.4];

// ── 컴포넌트 ──────────────────────────────────────────────────
export default function MapView({ session }) {
  const [tileId,        setTileId]        = useState(() => {
    const saved = getCookie('uam_map_tile');
    return TILE_STYLES.some(t => t.id === saved) ? saved : 'minimal';
  });
  const [selectedVtp,       setSelectedVtp]       = useState(null);
  const [selectedUavId,     setSelectedUavId]     = useState(null);
  const [excludeFinished,   setExcludeFinished]   = useState(false);
  const [mouseCoords,   setMouseCoords]   = useState(null);
  const [copyToast,     setCopyToast]     = useState(false);
  const [ctxMenu,       setCtxMenu]       = useState(null);
  const [filterOpen,    setFilterOpen]    = useState(false);
  const [showLayers,    setShowLayers]    = useState(() => {
    const defaults = { vertiport: true, fix: true, airway: true, airwayName: true };
    try {
      const saved = getCookie('uam_map_layers');
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch {}
    return defaults;
  });

  // 편집 모달
  const [fixCreateModal,  setFixCreateModal]  = useState(null); // { latlng }
  const [fixEditModal,    setFixEditModal]    = useState(null); // { fix }
  const [vtpEditModal,    setVtpEditModal]    = useState(null); // { vtp }
  const [airwayNameModal, setAirwayNameModal] = useState(null); // { from, to } — LINK 모드 생성
  const [airwayEditModal, setAirwayEditModal] = useState(null); // { airway }
  const [deleteConfirm,   setDeleteConfirm]   = useState(null); // { entityType, entityName, connectedAirways, onConfirm }

  // LINK 모드
  const [linkMode, setLinkMode] = useState(false);
  const [linkFrom, setLinkFrom] = useState(null); // { type, uid, name, lat, lng }

  const toastTimer   = useRef(null);
  const ctxTargetRef = useRef(null);
  const mapRef       = useRef(null);

  function toggleLayer(layer) {
    setShowLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  }

  useEffect(() => { setCookie('uam_map_tile', tileId); }, [tileId]);
  useEffect(() => { setCookie('uam_map_layers', JSON.stringify(showLayers)); }, [showLayers]);

  const [vertiports,      setVertiports]      = useState([]);
  const [fixPoints,       setFixPoints]       = useState([]);
  const [airways,         setAirways]         = useState([]);
  const [uavPositions,     setUavPositions]    = useState([]);
  const [flightPlans,     setFlightPlans]     = useState([]);
  const [aircrafts,       setAircrafts]       = useState([]);
  const [selectedPlanUid,    setSelectedPlanUid]    = useState(null);
  const [planRouteCache,     setPlanRouteCache]     = useState({});
  const [selectedUavPlanUid, setSelectedUavPlanUid] = useState(null);
  const [selectedUavPlan,    setSelectedUavPlan]    = useState(null);
  const [trackCache,         setTrackCache]         = useState({});

  // 선택된 기체가 운항 종료로 목록에서 사라지면 선택 해제
  useEffect(() => {
    if (!selectedUavId) return;
    if (!uavPositions.some(u => u.aircraftUid === selectedUavId)) {
      setSelectedUavId(null);
      setSelectedUavPlanUid(null);
      setSelectedUavPlan(null);
    }
  }, [uavPositions, selectedUavId]);

  // ESC: LINK 모드 취소
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return;
      if (linkFrom) setLinkFrom(null);
      else if (linkMode) setLinkMode(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [linkMode, linkFrom]);

  useEffect(() => { loadMapData(); }, []);

  useEffect(() => {
    async function pollPositions() {
      try {
        const res = await api.get('/api/aircraft/positions/active');
        const now = Date.now();
        setUavPositions(res.data.map(uav => ({
          ...uav,
          status: (now - new Date(uav.lastReceivedAt).getTime()) > 60000 ? 'SIGNAL LOST' : 'ACTIVE',
        })));
      } catch {
        // 네트워크 실패 시 기존 마커 유지 — 상태 초기화 금지
      }
    }
    pollPositions();
    const tid = setInterval(pollPositions, 3000);
    return () => clearInterval(tid);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedPlanUid) return;
    if (planRouteCache[selectedPlanUid]) return;
    api.get(`/api/flight-plan/${selectedPlanUid}/route`)
      .then(res => setPlanRouteCache(prev => ({ ...prev, [selectedPlanUid]: res.data })))
      .catch(() => {});
  }, [selectedPlanUid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedUavPlanUid) return;
    if (planRouteCache[selectedUavPlanUid]) return;
    api.get(`/api/flight-plan/${selectedUavPlanUid}/route`)
      .then(res => setPlanRouteCache(prev => ({ ...prev, [selectedUavPlanUid]: res.data })))
      .catch(() => {});
  }, [selectedUavPlanUid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedUavPlanUid) return;
    if (trackCache[selectedUavPlanUid]) return;
    api.get(`/api/flight-plan/${selectedUavPlanUid}/track`)
      .then(res => setTrackCache(prev => ({ ...prev, [selectedUavPlanUid]: res.data })))
      .catch(() => {});
  }, [selectedUavPlanUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // 선택된 기체의 실제 이동경로 15초(5틱)마다 갱신
  useEffect(() => {
    if (!selectedUavPlanUid) return;
    const tid = setInterval(() => {
      api.get(`/api/flight-plan/${selectedUavPlanUid}/track`)
        .then(res => setTrackCache(prev => ({ ...prev, [selectedUavPlanUid]: res.data })))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(tid);
  }, [selectedUavPlanUid]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMapData() {
    try {
      const [vtpRes, fixRes, awRes, fpRes, acRes] = await Promise.all([
        api.get('/api/vertiport/list'),
        api.get('/api/fix/list'),
        api.get('/api/airway/list'),
        api.get('/api/flight-plan/list'),
        api.get('/api/aircraft/list'),
      ]);
      setVertiports(vtpRes.data);
      setFixPoints(fixRes.data);
      setAirways(awRes.data);
      setFlightPlans(fpRes.data);
      setAircrafts(acRes.data);
    } catch (e) {
      console.error('지도 데이터 로드 실패', e);
    }
  }

  async function refreshAirways() {
    const res = await api.get('/api/airway/list');
    setAirways(res.data);
  }

  async function refreshFixAndAirways() {
    const [fixRes, awRes] = await Promise.all([
      api.get('/api/fix/list'),
      api.get('/api/airway/list'),
    ]);
    setFixPoints(fixRes.data);
    setAirways(awRes.data);
  }

  async function refreshVertiports() {
    const res = await api.get('/api/vertiport/list');
    setVertiports(res.data);
  }

  function findAc(uid) { return aircrafts.find(a => String(a.aircraftUid) === String(uid)); }
  function findVtpByUid(uid) { return vertiports.find(v => String(v.vertiportUid) === String(uid)); }

  function getConnectedAirways(type, uid) {
    return airways.filter(aw =>
      (aw.from.type === type && aw.from.uid === uid) ||
      (aw.to.type === type && aw.to.uid === uid)
    );
  }

  // groupName → 색상 매핑 (렌더 시마다 계산)
  function buildGroupColorMap() {
    const map = new Map();
    let i = 0;
    airways.forEach(aw => {
      const key = aw.groupName || '';
      if (!map.has(key)) map.set(key, GROUP_COLORS[i++ % GROUP_COLORS.length]);
    });
    return map;
  }

  // ── API 조작 ──────────────────────────────────────────────────
  async function handleDeleteFix(fixUid) {
    try { await api.delete(`/api/fix/${fixUid}`); await refreshFixAndAirways(); }
    catch (e) { console.error('FIX 삭제 실패', e); }
  }

  async function handleDeleteVertiport(vertiportUid) {
    try {
      await api.delete(`/api/vertiport/${vertiportUid}`);
      if (selectedVtp?.vertiportUid === vertiportUid) setSelectedVtp(null);
      await refreshVertiports();
    } catch (e) { console.error('버티포트 삭제 실패', e); }
  }

  async function handleDeleteAirway(airwayUid) {
    try { await api.delete(`/api/airway/${airwayUid}`); await refreshAirways(); }
    catch (e) { console.error('Airway 삭제 실패', e); }
  }

  // ── 컨텍스트 메뉴 ────────────────────────────────────────────
  function openCtxMenu(e, target) {
    const POPUP_W = 220, POPUP_H = 130;
    const cx = e.originalEvent.clientX;
    const cy = e.originalEvent.clientY;
    setCtxMenu({
      latlng:  e.latlng,
      x:       cx + POPUP_W > window.innerWidth  ? cx - POPUP_W : cx + 2,
      y:       cy + POPUP_H > window.innerHeight ? cy - POPUP_H : cy + 2,
      target:  target || null,
      confirm: false,
    });
  }

  function handleMapContextMenu(e) {
    if (session?.authLevel === 2) {
      openCtxMenu(e, null);
    } else {
      const text = `${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`;
      navigator.clipboard.writeText(text).then(() => {
        clearTimeout(toastTimer.current);
        setCopyToast(true);
        toastTimer.current = setTimeout(() => setCopyToast(false), 1800);
      });
    }
  }

  // ── LINK 모드 노드 선택 ───────────────────────────────────────
  function handleLinkClick(node) {
    if (!linkFrom) {
      setLinkFrom(node);
    } else {
      if (linkFrom.type === node.type && linkFrom.uid === node.uid) {
        setLinkFrom(null);
        return;
      }
      setAirwayNameModal({ from: linkFrom, to: node });
      setLinkFrom(null);
    }
  }

  // ── 렌더 ──────────────────────────────────────────────────────
  const groupColorMap = buildGroupColorMap();
  const existingGroups = [...new Set(airways.map(aw => aw.groupName).filter(Boolean))];

  return (
    <div className="map-wrap">
      <MapContainer ref={mapRef} center={CENTER} zoom={10} className={`map-container${linkMode ? ' link-mode' : ''}`} zoomControl>
        <TileLayer
          key={tileId}
          attribution={TILE_STYLES.find(t => t.id === tileId).attribution}
          url={TILE_STYLES.find(t => t.id === tileId).url}
        />
        <MouseCoords
          onMove={setMouseCoords}
          onContextMenu={handleMapContextMenu}
          onClose={() => { setCtxMenu(null); setLinkFrom(null); }}
          ctxTargetRef={ctxTargetRef}
        />

        {/* 선택된 비행계획 예상 경로 오버레이 (A* 실제 경로) */}
        {selectedPlanUid && (() => {
          const routeData = planRouteCache[selectedPlanUid];
          if (!routeData || !routeData.waypoints || routeData.waypoints.length < 2) return null;
          const pts = routeData.waypoints.map(w => [Number(w.lat), Number(w.lng)]);
          return (
            <>
              <Polyline
                key={`route-${selectedPlanUid}`}
                positions={pts}
                pathOptions={{ color: '#00d46a', weight: 4, opacity: 0.85, lineCap: 'round', lineJoin: 'round' }}
              />
              {routeData.waypoints
                .filter(w => w.nodeType === 'fix')
                .map(w => {
                  const fixName = fixPoints.find(f => String(f.fixUid) === String(w.nodeUid))?.fixId ?? `FIX-${w.nodeUid}`;
                  return (
                    <Marker
                      key={`rfix-${w.nodeUid}-${w.seq}`}
                      position={[Number(w.lat), Number(w.lng)]}
                      icon={routeFixIcon(fixName)}
                    />
                  );
                })
              }
            </>
          );
        })()}

        {/* UAV 선택 시 비행계획 전체 경로 (출발 → 도착, 실선) */}
        {selectedUavPlanUid && (() => {
          const routeData = planRouteCache[selectedUavPlanUid];
          if (!routeData?.waypoints?.length) return null;
          const pts = routeData.waypoints.map(w => [Number(w.lat), Number(w.lng)]);
          if (pts.length < 2) return null;
          return (
            <Polyline
              key={`uav-route-${selectedUavPlanUid}`}
              positions={pts}
              pathOptions={{ color: '#fbbf24', weight: 5, opacity: 0.55, lineCap: 'round', lineJoin: 'round' }}
            />
          );
        })()}

        {/* UAV 선택 시 실제 이동경로 (빨간 실선) */}
        {selectedUavPlanUid && (() => {
          const trackData = trackCache[selectedUavPlanUid];
          if (!trackData?.track?.length) return null;
          const pts = trackData.track.map(p => [Number(p.lat), Number(p.lng)]);
          if (pts.length < 2) return null;
          return (
            <Polyline
              key={`uav-track-${selectedUavPlanUid}`}
              positions={pts}
              pathOptions={{ color: '#ff4d4d', weight: 2, opacity: 1, lineCap: 'round', lineJoin: 'round' }}
            />
          );
        })()}

        {/* 노선 폴리라인 — groupName별 색상 */}
        {showLayers.airway && airways.map(aw => {
          const color = groupColorMap.get(aw.groupName || '') || GROUP_COLORS[0];
          return (
            <Polyline
              key={`aw-line-${aw.airwayUid}`}
              positions={[
                [Number(aw.from.lat), Number(aw.from.lng)],
                [Number(aw.to.lat),   Number(aw.to.lng)],
              ]}
              pathOptions={{ color, weight: 1.8, opacity: 0.6, dashArray: '6 4' }}
            />
          );
        })}

        {/* 노선 이름 레이블 마커 */}
        {showLayers.airway && showLayers.airwayName && airways.map(aw => {
          const color = groupColorMap.get(aw.groupName || '') || GROUP_COLORS[0];
          return (
            <Marker
              key={`aw-label-${aw.airwayUid}`}
              position={[
                (Number(aw.from.lat) + Number(aw.to.lat)) / 2,
                (Number(aw.from.lng) + Number(aw.to.lng)) / 2,
              ]}
              icon={airwayLabelIcon(aw.groupName, aw.name, color)}
              eventHandlers={{
                contextmenu: e => {
                  if (session?.authLevel !== 2) return;
                  e.originalEvent.preventDefault();
                  ctxTargetRef.current = true;
                  openCtxMenu(e, { type: 'airway', data: aw });
                },
              }}
            />
          );
        })}

        {/* 버티포트 마커 */}
        {showLayers.vertiport && vertiports.map(vtp => (
          <Marker key={vtp.vertiportUid}
            position={[Number(vtp.lat), Number(vtp.lng)]}
            icon={vtpIcon(vtp.status, selectedVtp?.vertiportUid === vtp.vertiportUid)}
            eventHandlers={{
              click: () => {
                if (linkMode && session?.authLevel === 2) {
                  handleLinkClick({ type: 'vertiport', uid: vtp.vertiportUid, name: vtp.name, lat: Number(vtp.lat), lng: Number(vtp.lng) });
                } else {
                  setSelectedUavId(null); setSelectedUavPlanUid(null); setSelectedUavPlan(null);
                  setSelectedVtp(vtp);
                  setSelectedPlanUid(null);
                }
              },
              contextmenu: e => {
                if (session?.authLevel !== 2) return;
                e.originalEvent.preventDefault();
                ctxTargetRef.current = true;
                openCtxMenu(e, { type: 'vertiport', data: vtp });
              },
            }}
          />
        ))}

        {/* FIX 마커 */}
        {showLayers.fix && fixPoints.map(fix => (
          <Marker key={fix.fixUid}
            position={[Number(fix.lat), Number(fix.lng)]}
            icon={fixIcon(fix.fixId)}
            eventHandlers={{
              click: e => {
                if (linkMode && session?.authLevel === 2) {
                  e.target.closePopup();
                  handleLinkClick({ type: 'fix', uid: fix.fixUid, name: fix.fixId, lat: Number(fix.lat), lng: Number(fix.lng) });
                }
              },
              contextmenu: e => {
                if (session?.authLevel !== 2) return;
                e.originalEvent.preventDefault();
                ctxTargetRef.current = true;
                openCtxMenu(e, { type: 'fix', data: fix });
              },
            }}
          >
            <Popup className="vtp-popup">
              <div className="vtp-popup-inner">
                <span className="vtp-popup-name">{fix.fixId}</span>
                <span className="vtp-popup-id">{Number(fix.lat).toFixed(5)}, {Number(fix.lng).toFixed(5)}</span>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* UAV 마커 — 근접 경고 계산 후 렌더 */}
        {(() => {
          const alertMap = new Map();
          for (let i = 0; i < uavPositions.length; i++) {
            for (let j = i + 1; j < uavPositions.length; j++) {
              const a = uavPositions[i], b = uavPositions[j];
              const d = calcDist(Number(a.lat), Number(a.lng), Number(b.lat), Number(b.lng));
              const lvl = d < 500 ? 'danger' : d < 2000 ? 'warning' : null;
              if (lvl) {
                if (lvl === 'danger' || !alertMap.has(a.aircraftUid)) alertMap.set(a.aircraftUid, lvl);
                if (lvl === 'danger' || !alertMap.has(b.aircraftUid)) alertMap.set(b.aircraftUid, lvl);
              }
            }
          }
          return uavPositions.map(uav => (
            <Marker key={uav.aircraftUid}
              position={[Number(uav.lat), Number(uav.lng)]}
              icon={uavIcon(uav.aircraftId, uav.status, selectedUavId === uav.aircraftUid, alertMap.get(uav.aircraftUid))}
              eventHandlers={{
                click: async () => {
                  if (selectedUavId === uav.aircraftUid) {
                    setSelectedUavId(null); setSelectedUavPlanUid(null); setSelectedUavPlan(null); return;
                  }
                  setSelectedVtp(null);
                  setSelectedUavId(uav.aircraftUid);
                  setSelectedUavPlan(null);

                  function applyPlan(plan) {
                    setTrackCache(prev => { const n = {...prev}; delete n[plan.flightPlanUid]; return n; });
                    setSelectedUavPlanUid(plan.flightPlanUid);
                    setSelectedUavPlan(plan);
                  }

                  try {
                    // 1) 운항중(status=2) 목록에서 직접 검색
                    const activeRes = await api.get('/api/flight-plan/active');
                    const activePlan = activeRes.data.find(fp => String(fp.aircraftUid) === String(uav.aircraftUid));
                    if (activePlan) { applyPlan(activePlan); return; }

                    // 2) 기체의 현재 비행경로 API → flightPlanUid 추출 후 상세 조회
                    //    (status 1 또는 2인 plan을 날짜 무관하게 직접 검색)
                    try {
                      const routeRes = await api.get(`/api/aircraft/${uav.aircraftUid}/flight-route`);
                      const planUid = routeRes.data?.flightPlanUid;
                      if (planUid) {
                        const planRes = await api.get(`/api/flight-plan/${planUid}`);
                        if (planRes.data?.flightPlanUid) { applyPlan(planRes.data); return; }
                      }
                    } catch { /* route 없을 수 있음 */ }

                    // 3) 최근 30일 목록에서 status 1·2 중 가장 최신 plan
                    const today = new Date().toISOString().slice(0, 10);
                    const past  = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
                    const listRes = await api.get(`/api/flight-plan/list?start=${past}&end=${today}`);
                    const found = listRes.data
                      .filter(fp => String(fp.aircraftUid) === String(uav.aircraftUid) && (fp.status === 1 || fp.status === 2))
                      .sort((a, b) => b.flightPlanUid - a.flightPlanUid)[0] ?? null;
                    if (found) { applyPlan(found); return; }

                    setSelectedUavPlanUid(null);
                    setSelectedUavPlan(null);
                  } catch {
                    setSelectedUavPlanUid(null);
                    setSelectedUavPlan(null);
                  }
                },
              }}
            />
          ));
        })()}

        {/* 우클릭 위치 점 (빈 지도) */}
        {ctxMenu && !ctxMenu.target && (
          <CircleMarker center={ctxMenu.latlng} radius={5}
            pathOptions={{ color: '#4d9fff', fillColor: '#4d9fff', fillOpacity: 1, weight: 1.5 }} />
        )}

        {/* LINK 모드 — FROM 노드 강조 */}
        {linkMode && linkFrom && (
          <CircleMarker center={[linkFrom.lat, linkFrom.lng]} radius={13}
            pathOptions={{ color: '#00d46a', fill: false, weight: 2, dashArray: '5 3', opacity: 0.9 }} />
        )}

        {/* LINK 모드 — 미리보기 점선 */}
        {linkMode && linkFrom && mouseCoords && (
          <Polyline
            positions={[[linkFrom.lat, linkFrom.lng], [mouseCoords.lat, mouseCoords.lng]]}
            pathOptions={{ color: '#00d46a', weight: 1.5, opacity: 0.6, dashArray: '6 4' }}
          />
        )}
      </MapContainer>

      {/* 상단 컨트롤 */}
      <div className="map-controls">
        <div className="map-switcher">
          <div className="map-switcher-tiles">
            {TILE_STYLES.map(t => (
              <button key={t.id}
                className={`map-switch-btn${tileId === t.id ? ' active' : ''}`}
                onClick={() => setTileId(t.id)}
              >{t.label}</button>
            ))}
          </div>
          <div className="map-switcher-actions">
            <button className="map-switch-btn map-refresh-btn" onClick={loadMapData} title="지도 데이터 갱신">↺ SYNC</button>
            <div className="map-switcher-sep" />
            <button
              className={`map-switch-btn map-filter-btn${filterOpen ? ' active' : ''}`}
              onClick={() => setFilterOpen(v => !v)}
            >⊞ FILTER</button>
            {session?.authLevel === 2 && (
              <>
                <div className="map-switcher-sep" />
                <button
                  className={`map-switch-btn map-link-btn${linkMode ? ' active' : ''}`}
                  onClick={() => { setLinkMode(v => !v); setLinkFrom(null); }}
                  title="Airway 연결 모드 (ESC로 취소)"
                >⛓ LINK</button>
              </>
            )}
          </div>
        </div>
        {filterOpen && (
          <div className="map-filter-panel">
            {[
              { key: 'vertiport', label: '버티포트' },
              { key: 'fix',       label: 'FIX' },
              { key: 'airway',    label: 'AIRWAY' },
            ].map(({ key, label }) => (
              <label key={key} className="map-filter-item" onClick={() => toggleLayer(key)}>
                <span className={`map-filter-cb${showLayers[key] ? ' checked' : ''}`} />
                <span className="map-filter-label">{label}</span>
              </label>
            ))}
            <label
              className={`map-filter-item map-filter-sub${!showLayers.airway ? ' disabled' : ''}`}
              onClick={() => showLayers.airway && toggleLayer('airwayName')}
            >
              <span className={`map-filter-cb${showLayers.airwayName && showLayers.airway ? ' checked' : ''}`} />
              <span className="map-filter-label">AIRWAY-NAME</span>
            </label>
          </div>
        )}
      </div>

      {/* LINK 모드 힌트 배너 */}
      {linkMode && (
        <div className="map-link-banner">
          {linkFrom
            ? <>출발: <strong>{linkFrom.name}</strong> — 도착 노드를 클릭하세요</>
            : '출발 노드 (FIX / 버티포트)를 클릭하세요'}
          <button className="map-link-banner-cancel" onClick={() => { setLinkMode(false); setLinkFrom(null); }}>✕ 취소</button>
        </div>
      )}

      {/* 버티포트 사이드 패널 */}
      {selectedVtp && (
        <div className="vtp-panel">
          <div className="vtp-panel-hd">
            <button className="vtp-panel-close" onClick={() => { setSelectedVtp(null); setSelectedPlanUid(null); }}>✕</button>
            <div className="vtp-panel-title-wrap">
              <span className="vtp-panel-id">{selectedVtp.vertiportId}</span>
              <span className="vtp-panel-name">{selectedVtp.name}</span>
            </div>
            <span className={`vtp-panel-badge ${selectedVtp.status === 1 ? 'active' : 'standby'}`}>
              {selectedVtp.status === 1 ? 'ACTIVE' : 'STANDBY'}
            </span>
          </div>
          <div className="vtp-panel-info">
            <div className="vtp-panel-desc">{selectedVtp.description}</div>
            <div className="vtp-panel-stats">
              {(() => {
                const vtpUid = selectedVtp.vertiportUid;
                const cnt = flightPlans.filter(fp =>
                  String(fp.depVertiportUid) === String(vtpUid) ||
                  String(fp.arrVertiportUid) === String(vtpUid)
                ).length;
                return (
                  <>
                    <div className="vtp-stat"><span className="vtp-stat-lbl">RUNWAYS</span><span className="vtp-stat-val">{selectedVtp.runways}</span></div>
                    <div className="vtp-stat"><span className="vtp-stat-lbl">CAPACITY</span><span className="vtp-stat-val">{selectedVtp.capacity}</span></div>
                    <div className="vtp-stat"><span className="vtp-stat-lbl">SCHEDULED</span><span className="vtp-stat-val">{cnt}</span></div>
                  </>
                );
              })()}
            </div>
          </div>
          <div className="vtp-panel-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>FLIGHT SCHEDULE</span>
            <label className="vtp-exclude-check">
              <input
                type="checkbox"
                checked={excludeFinished}
                onChange={e => setExcludeFinished(e.target.checked)}
              />
              종료된 비행 제외
            </label>
          </div>
          <div className="vtp-schedule-list">
            {(() => {
              const vtpUid = selectedVtp.vertiportUid;
              const isActive = fp => fp.status === 0 || fp.status === 1 || fp.status === 2;
              const depPlans = flightPlans.filter(fp => String(fp.depVertiportUid) === String(vtpUid) && (!excludeFinished || isActive(fp)));
              const arrPlans = flightPlans.filter(fp => String(fp.arrVertiportUid) === String(vtpUid) && (!excludeFinished || isActive(fp)));
              if (depPlans.length === 0 && arrPlans.length === 0) {
                return <div className="vtp-schedule-empty">스케줄 없음</div>;
              }
              function planItem(fp, dir) {
                const ac = findAc(fp.aircraftUid);
                const partnerUid = dir === 'dep' ? fp.arrVertiportUid : fp.depVertiportUid;
                const partner = findVtpByUid(partnerUid);
                const time = dir === 'dep' ? fp.scheduledDepTime : fp.scheduledArrTime;
                const approved = fp.status === 1;
                const sel = selectedPlanUid === fp.flightPlanUid;
                return (
                  <div key={fp.flightPlanUid}
                    className={`vtp-schedule-item${approved ? ' fp-selectable' : ''}${sel ? ' fp-route-sel' : ''}`}
                    onClick={() => { if (approved) setSelectedPlanUid(p => p === fp.flightPlanUid ? null : fp.flightPlanUid); }}
                  >
                    <div className="vtp-sch-left">
                      <span className={`vtp-sch-type ${dir}`}>{dir === 'dep' ? '▲ 출발' : '▼ 도착'}</span>
                      <span className="vtp-sch-peer">{partner?.name ?? `VTP-${partnerUid}`}</span>
                    </div>
                    <div className="vtp-sch-right">
                      <span className="vtp-sch-uav">{ac?.aircraftId ?? `#${fp.aircraftUid}`}</span>
                      <span className="vtp-sch-time">{time ? time.replace('T', ' ').slice(11, 16) : '-'}</span>
                      <span className={`vtp-sch-badge vtp-sch-s${fp.status}`}>{STATUS_LABELS[fp.status] ?? fp.status}</span>
                    </div>
                  </div>
                );
              }
              return (
                <>
                  {depPlans.length > 0 && (
                    <>
                      <div className="vtp-sch-group-hd">출발 ({depPlans.length})</div>
                      {depPlans.map(fp => planItem(fp, 'dep'))}
                    </>
                  )}
                  {arrPlans.length > 0 && (
                    <>
                      <div className="vtp-sch-group-hd">도착 ({arrPlans.length})</div>
                      {arrPlans.map(fp => planItem(fp, 'arr'))}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* UAV 사이드 패널 */}
      {selectedUavId && (() => {
        const uav = uavPositions.find(u => u.aircraftUid === selectedUavId);
        if (!uav) return null;
        const plan = selectedUavPlan;
        const depVtp = plan ? findVtpByUid(plan.depVertiportUid) : null;
        const arrVtp = plan ? findVtpByUid(plan.arrVertiportUid) : null;
        const isLost = uav.status === 'SIGNAL LOST';
        return (
          <div className="vtp-panel">
            <div className="vtp-panel-hd">
              <button className="vtp-panel-close" onClick={() => { setSelectedUavId(null); setSelectedUavPlanUid(null); setSelectedUavPlan(null); }}>✕</button>
              <div className="vtp-panel-title-wrap">
                <span className="vtp-panel-id">{uav.aircraftId}</span>
                <span className="vtp-panel-name">{uav.modelName ?? '—'}</span>
              </div>
              <span className={`vtp-panel-badge ${isLost ? 'standby' : 'active'}`}>
                {isLost ? 'SIGNAL LOST' : 'ACTIVE'}
              </span>
            </div>
            <div className="vtp-panel-info">
              <div className="vtp-panel-stats">
                <div className="vtp-stat"><span className="vtp-stat-lbl">ALT</span><span className="vtp-stat-val">{uav.alt ?? '-'} m</span></div>
                <div className="vtp-stat"><span className="vtp-stat-lbl">SPEED</span><span className="vtp-stat-val">{uav.speed != null ? (uav.speed * 0.539957).toFixed(1) : '-'} kt</span></div>
                <div className="vtp-stat"><span className="vtp-stat-lbl">HDG</span><span className="vtp-stat-val">{uav.heading ?? '-'}°</span></div>
              </div>
              <div className="vtp-panel-desc" style={{ fontFamily: 'monospace', fontSize: 11, color: '#4a6a4a', marginTop: 4 }}>
                최종수신 {uav.lastReceivedAt ? new Date(uav.lastReceivedAt).toLocaleTimeString('ko-KR', { hour12: false }) : '-'}
              </div>
            </div>
            <div className="vtp-panel-section-label">FLIGHT PLAN</div>
            <div className="vtp-schedule-list">
              {plan ? (
                <>
                  <div className="vtp-schedule-item">
                    <div className="vtp-sch-left">
                      <span className="vtp-sch-type dep">▲ 출발</span>
                      <span className="vtp-sch-peer">{depVtp?.name ?? `VTP-${plan.depVertiportUid}`}</span>
                    </div>
                    <div className="vtp-sch-right">
                      <span className="vtp-sch-time">{plan.scheduledDepTime?.replace('T', ' ').slice(0, 16) ?? '-'}</span>
                    </div>
                  </div>
                  <div className="vtp-schedule-item">
                    <div className="vtp-sch-left">
                      <span className="vtp-sch-type arr">▼ 도착</span>
                      <span className="vtp-sch-peer">{arrVtp?.name ?? `VTP-${plan.arrVertiportUid}`}</span>
                    </div>
                    <div className="vtp-sch-right">
                      <span className="vtp-sch-time">{plan.scheduledArrTime?.replace('T', ' ').slice(0, 16) ?? '-'}</span>
                    </div>
                  </div>
                  <div style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 10, color: '#3a5a7a' }}>
                    planUID {plan.flightPlanUid} · {STATUS_LABELS[plan.status] ?? plan.status}
                  </div>
                  <div style={{ padding: '0 10px 8px', display: 'flex', gap: 8 }}>
                    <span className={`vtp-sch-badge vtp-sch-s${plan.status}`}>{STATUS_LABELS[plan.status] ?? plan.status}</span>
                  </div>
                  {planRouteCache[selectedUavPlanUid]?.waypoints?.length > 0 && (() => {
                    const wps = planRouteCache[selectedUavPlanUid].waypoints;
                    return (
                      <>
                        <div className="vtp-panel-section-label">ROUTE WAYPOINTS</div>
                        <div className="uav-route-table-wrap">
                          <table className="uav-route-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>구분</th>
                                <th>식별자</th>
                                <th>Airway</th>
                                <th>위도</th>
                                <th>경도</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wps.map(w => {
                                const name = w.nodeType === 'fix'
                                  ? (fixPoints.find(f => String(f.fixUid) === String(w.nodeUid))?.fixId ?? `FIX-${w.nodeUid}`)
                                  : (vertiports.find(v => String(v.vertiportUid) === String(w.nodeUid))?.name ?? `VTP-${w.nodeUid}`);
                                const aw = w.airwayUid ? airways.find(a => String(a.airwayUid) === String(w.airwayUid)) : null;
                                const lat = Number(w.lat), lng = Number(w.lng);
                                return (
                                  <tr key={w.seq} className="rwt-clickable"
                                    onClick={() => mapRef.current?.flyTo([lat, lng], 14, { animate: true, duration: 0.8 })}
                                  >
                                    <td>{w.seq}</td>
                                    <td className={`rwt-type ${w.nodeType}`}>{w.nodeType === 'fix' ? 'FIX' : 'VTP'}</td>
                                    <td className="rwt-name">{name}</td>
                                    <td className="rwt-aw">{aw ? aw.name : '—'}</td>
                                    <td>{lat.toFixed(5)}</td>
                                    <td>{lng.toFixed(5)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  })()}
                </>
              ) : (
                <div className="vtp-schedule-empty">활성 비행계획 없음</div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 좌표 복사 토스트 */}
      {copyToast && <div className="map-copy-toast">좌표 복사됨</div>}

      {/* 우클릭 컨텍스트 메뉴 */}
      {ctxMenu && (
        <>
          <div className="map-ctx-overlay" onClick={() => setCtxMenu(null)} />
          <div className="map-ctx-menu" style={{ left: ctxMenu.x, top: ctxMenu.y }}>

            {/* FIX */}
            {ctxMenu.target?.type === 'fix' && (() => {
              const fix = ctxMenu.target.data;
              return <>
                <div className="map-ctx-entity fix">
                  <span className="map-ctx-entity-icon">◆</span>
                  <span className="map-ctx-entity-name">{fix.fixId}</span>
                </div>
                <div className="map-ctx-divider" />
                <button className="map-ctx-btn" onClick={() => { setFixEditModal({ fix }); setCtxMenu(null); }}>FIX 수정</button>
                <button className="map-ctx-btn" onClick={() => {
                  setCtxMenu(null);
                  setDeleteConfirm({
                    entityType: 'fix',
                    entityName: fix.fixId,
                    connectedAirways: getConnectedAirways('fix', fix.fixUid),
                    onConfirm: async () => { await handleDeleteFix(fix.fixUid); setDeleteConfirm(null); },
                  });
                }}>FIX 삭제</button>
              </>;
            })()}

            {/* 버티포트 */}
            {ctxMenu.target?.type === 'vertiport' && (() => {
              const vtp = ctxMenu.target.data;
              const color = vtp.status === 1 ? '#00d46a' : '#f0b429';
              return <>
                <div className="map-ctx-entity vtp">
                  <span className="map-ctx-entity-icon" style={{ color }}>●</span>
                  <span className="map-ctx-entity-name">{vtp.name}</span>
                </div>
                <div className="map-ctx-divider" />
                <button className="map-ctx-btn" onClick={() => { setVtpEditModal({ vtp }); setCtxMenu(null); }}>버티포트 수정</button>
                <button className="map-ctx-btn" onClick={() => {
                  setCtxMenu(null);
                  setDeleteConfirm({
                    entityType: 'vertiport',
                    entityName: vtp.name,
                    connectedAirways: getConnectedAirways('vertiport', vtp.vertiportUid),
                    onConfirm: async () => { await handleDeleteVertiport(vtp.vertiportUid); setDeleteConfirm(null); },
                  });
                }}>버티포트 삭제</button>
              </>;
            })()}

            {/* Airway */}
            {ctxMenu.target?.type === 'airway' && (() => {
              const aw = ctxMenu.target.data;
              const color = groupColorMap.get(aw.groupName || '') || GROUP_COLORS[0];
              return <>
                <div className="map-ctx-entity airway">
                  <span className="map-ctx-entity-icon" style={{ color }}>—</span>
                  <span className="map-ctx-entity-name">{aw.name}</span>
                  {aw.groupName && <span className="map-ctx-entity-group">{aw.groupName}</span>}
                </div>
                <div className="map-ctx-divider" />
                <button className="map-ctx-btn" onClick={() => { setAirwayEditModal({ airway: aw }); setCtxMenu(null); }}>Airway 이름 수정</button>
                {ctxMenu.confirm
                  ? <button className="map-ctx-btn map-ctx-btn-danger" onClick={async () => { setCtxMenu(null); await handleDeleteAirway(aw.airwayUid); }}>삭제 확인</button>
                  : <button className="map-ctx-btn" onClick={() => setCtxMenu(p => ({ ...p, confirm: true }))}>Airway 삭제</button>}
              </>;
            })()}

            {/* 빈 지도 (관리자) */}
            {!ctxMenu.target && <>
              <div className="map-ctx-coords">
                <span>{ctxMenu.latlng.lat.toFixed(5)}</span>
                <span className="map-ctx-sep">,</span>
                <span>{ctxMenu.latlng.lng.toFixed(5)}</span>
              </div>
              <div className="map-ctx-divider" />
              <button className="map-ctx-btn" onClick={() => setCtxMenu(null)}>현재 위치 버티포트로 등록</button>
              <button className="map-ctx-btn" onClick={() => { setFixCreateModal({ latlng: ctxMenu.latlng }); setCtxMenu(null); }}>현재 위치 FIX로 등록</button>
            </>}
          </div>
        </>
      )}

      {/* FIX 등록 모달 */}
      {fixCreateModal && (
        <FixCreateModal
          latlng={fixCreateModal.latlng}
          onSave={async ({ fixId }) => {
            await api.post('/api/fix/create', { fixId, lat: fixCreateModal.latlng.lat, lng: fixCreateModal.latlng.lng });
            await refreshFixAndAirways();
            setFixCreateModal(null);
          }}
          onCancel={() => setFixCreateModal(null)}
        />
      )}

      {/* FIX 수정 모달 */}
      {fixEditModal && (
        <FixEditModal
          fix={fixEditModal.fix}
          onSave={async ({ fixId }) => {
            await api.put(`/api/fix/${fixEditModal.fix.fixUid}`, { fixId });
            await refreshFixAndAirways();
            setFixEditModal(null);
          }}
          onCancel={() => setFixEditModal(null)}
        />
      )}

      {/* 버티포트 수정 모달 */}
      {vtpEditModal && (
        <VtpEditModal
          vtp={vtpEditModal.vtp}
          onSave={async ({ name, status }) => {
            await api.put(`/api/vertiport/${vtpEditModal.vtp.vertiportUid}`, { name, status });
            await refreshVertiports();
            if (selectedVtp?.vertiportUid === vtpEditModal.vtp.vertiportUid)
              setSelectedVtp(prev => ({ ...prev, name, status }));
            setVtpEditModal(null);
          }}
          onCancel={() => setVtpEditModal(null)}
        />
      )}

      {/* Airway 생성 모달 (LINK 모드) */}
      {airwayNameModal && (
        <AirwayNameModal
          from={airwayNameModal.from}
          to={airwayNameModal.to}
          existingGroups={existingGroups}
          onSave={async (name, groupName) => {
            await api.post('/api/airway/create', {
              name,
              groupName: groupName || null,
              fromType: airwayNameModal.from.type,
              fromUid:  airwayNameModal.from.uid,
              toType:   airwayNameModal.to.type,
              toUid:    airwayNameModal.to.uid,
            });
            await refreshAirways();
            setAirwayNameModal(null);
          }}
          onCancel={() => { setAirwayNameModal(null); }}
        />
      )}

      {/* Airway 수정 모달 */}
      {airwayEditModal && (
        <AirwayEditModal
          airway={airwayEditModal.airway}
          existingGroups={existingGroups}
          onSave={async (name, groupName) => {
            await api.put(`/api/airway/${airwayEditModal.airway.airwayUid}`, { name, groupName: groupName || null });
            await refreshAirways();
            setAirwayEditModal(null);
          }}
          onCancel={() => setAirwayEditModal(null)}
        />
      )}

      {/* 삭제 확인 모달 */}
      {deleteConfirm && (
        <DeleteConfirmModal
          entityType={deleteConfirm.entityType}
          entityName={deleteConfirm.entityName}
          connectedAirways={deleteConfirm.connectedAirways}
          onConfirm={deleteConfirm.onConfirm}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* 범례 */}
      <div className={`map-legend${(selectedVtp || selectedUavId) ? ' map-legend-hidden' : ''}`}>
        <div className="map-legend-title">LEGEND</div>
        <div className="map-legend-item">
          <div className="map-legend-vtp-wrap" style={{ borderColor: '#38bdf8', boxShadow: '0 0 5px #38bdf888, 0 0 4px #00d46a66' }}>
            <img src="/vertiport.png" className="map-legend-vtp-icon" alt="" />
          </div>
          <span>VTP ACTIVE</span>
        </div>
        <div className="map-legend-item">
          <div className="map-legend-vtp-wrap" style={{ borderColor: '#38bdf8', boxShadow: '0 0 5px #38bdf888, 0 0 4px #f0b42966' }}>
            <img src="/vertiport.png" className="map-legend-vtp-icon" alt="" />
          </div>
          <span>VTP STANDBY</span>
        </div>
        <div className="map-legend-item"><span style={{ color: '#a78bfa', fontSize: 10, width: 10 }}>◆</span><span>FIX</span></div>
        <div className="map-legend-item"><span className="map-legend-line" /><span>AIRWAY</span></div>
        <div className="map-legend-divider" />
        <div className="map-legend-item"><span className="map-legend-uav" style={{ borderColor: '#00d46a' }} /><span>ON TIME</span></div>
        <div className="map-legend-item"><span className="map-legend-uav" style={{ borderColor: '#ff4d4d' }} /><span>SIGNAL LOST</span></div>
        <div className="map-legend-divider" />
        <div className="map-legend-coords">
          {mouseCoords ? `${mouseCoords.lat.toFixed(5)}, ${mouseCoords.lng.toFixed(5)}` : '— , —'}
        </div>
      </div>
    </div>
  );
}
